/**
 * Serviço para gerenciar perfil do usuário
 * Funciona tanto para usuários Google quanto CPF
 */

import { db, storage } from "../config/firebase";
import { doc, updateDoc, setDoc } from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { updateProfile } from "firebase/auth";
import errorLogService from "./errorLogService";

class PerfilService {
  /**
   * Atualizar foto de perfil do usuário
   * @param {File} file - Arquivo de imagem
   * @param {Object} currentUser - Usuário atual do Firebase Auth
   * @param {Object} churchUser - Dados do usuário da igreja
   * @returns {Promise<string>} URL da foto
   */
  async updatePhoto(file, currentUser, churchUser) {
    try {
      // Validar arquivo
      if (!file.type.startsWith("image/")) {
        throw new Error(`Tipo de arquivo inválido: ${file.type}`);
      }

      if (file.size > 5 * 1024 * 1024) {
        throw new Error(`Arquivo muito grande: ${file.size} bytes`);
      }

      // Determinar ID do usuário para upload
      const userId = currentUser?.uid || churchUser?.id || `user_${Date.now()}`;

      // Upload para Firebase Storage
      const photoRef = ref(storage, `profile-photos/${userId}`);
      await uploadBytes(photoRef, file);
      const photoURL = await getDownloadURL(photoRef);

      // Atualizar Firebase Auth se possível
      if (currentUser?.uid && typeof currentUser.getIdToken === "function") {
        try {
          await updateProfile(currentUser, { photoURL });
          await errorLogService.logError(
            new Error("Firebase Auth atualizado com sucesso"),
            {
              component: "PerfilService",
              action: "UpdateFirebaseAuthPhoto",
              userId: currentUser.uid,
              level: "success",
            }
          );
        } catch (authError) {
          await errorLogService.logError(authError, {
            component: "PerfilService",
            action: "UpdateFirebaseAuthPhotoError",
            userId: currentUser?.uid,
            errorCode: authError.code,
            note: "Continuando com atualização no Firestore",
          });
        }
      }

      // Sempre atualizar no Firestore
      await this.updateFirestorePhoto(photoURL, currentUser, churchUser);

      await errorLogService.logError(new Error("Foto atualizada com sucesso"), {
        component: "PerfilService",
        action: "UpdatePhotoSuccess",
        userId,
        fileSize: file.size,
        level: "success",
      });

      return photoURL;
    } catch (error) {
      await errorLogService.logError(error, {
        component: "PerfilService",
        action: "UpdatePhotoError",
        userId: currentUser?.uid || churchUser?.id,
        fileSize: file?.size,
        errorCode: error.code,
      });
      throw error;
    }
  }

  /**
   * Remover foto customizada e voltar para foto do Google ou fallback
   * @param {Object} currentUser - Usuário atual do Firebase Auth
   * @param {Object} churchUser - Dados do usuário da igreja
   * @returns {Promise<boolean>} Sucesso da operação
   */
  async removePhoto(currentUser, churchUser) {
    try {
      // Obter foto original do Google se existir
      const googlePhotoURL = currentUser?.providerData?.[0]?.photoURL || null;

      // Atualizar Firebase Auth com foto do Google ou null
      if (currentUser?.uid && typeof currentUser.getIdToken === "function") {
        try {
          await updateProfile(currentUser, { photoURL: googlePhotoURL });
          await errorLogService.logError(
            new Error("Foto removida do Firebase Auth"),
            {
              component: "PerfilService",
              action: "RemoveFirebaseAuthPhoto",
              userId: currentUser.uid,
              restoredGooglePhoto: !!googlePhotoURL,
              level: "success",
            }
          );
        } catch (authError) {
          await errorLogService.logError(authError, {
            component: "PerfilService",
            action: "RemoveFirebaseAuthPhotoError",
            userId: currentUser?.uid,
            errorCode: authError.code,
          });
        }
      }

      // Remover do Firestore (ou restaurar foto do Google)
      await this.updateFirestorePhoto(googlePhotoURL, currentUser, churchUser);

      await errorLogService.logError(new Error("Foto removida com sucesso"), {
        component: "PerfilService",
        action: "RemovePhotoSuccess",
        userId: currentUser?.uid || churchUser?.id,
        restoredGooglePhoto: !!googlePhotoURL,
        level: "success",
      });

      return true;
    } catch (error) {
      await errorLogService.logError(error, {
        component: "PerfilService",
        action: "RemovePhotoError",
        userId: currentUser?.uid || churchUser?.id,
        errorCode: error.code,
      });
      throw error;
    }
  }

  /**
   * Atualizar foto no Firestore
   * @param {string} photoURL - URL da foto
   * @param {Object} currentUser - Usuário atual
   * @param {Object} churchUser - Dados do usuário da igreja
   */
  async updateFirestorePhoto(photoURL, currentUser, churchUser) {
    const updates = { photoURL };

    // Determinar documento principal
    const userDocId = churchUser?.id || currentUser?.uid;
    if (userDocId) {
      try {
        const userDoc = doc(db, "users", userDocId);
        await setDoc(userDoc, updates, { merge: true });
      } catch (error) {
        await errorLogService.logError(error, {
          component: "PerfilService",
          action: "UpdateFirestoreUserPhoto",
          userId: userDocId,
          errorCode: error.code,
        });
      }
    }

    // Também atualizar na coleção membros se existir referência
    if (churchUser?.membroId) {
      try {
        const membroDoc = doc(db, "membros", churchUser.membroId);
        await updateDoc(membroDoc, updates);
      } catch (error) {
        await errorLogService.logError(error, {
          component: "PerfilService",
          action: "UpdateFirestoreMembroPhoto",
          membroId: churchUser.membroId,
          errorCode: error.code,
        });
      }
    }
  }

  /**
   * Atualizar nome de exibição do usuário
   * @param {string} displayName - Novo nome
   * @param {Object} currentUser - Usuário atual do Firebase Auth
   * @param {Object} churchUser - Dados do usuário da igreja
   */
  async updateDisplayName(displayName, currentUser, churchUser) {
    try {
      const trimmedName = displayName.trim();

      if (!trimmedName) {
        throw new Error("Nome não pode estar vazio");
      }

      // Atualizar Firebase Auth se possível
      if (currentUser?.uid && typeof currentUser.getIdToken === "function") {
        try {
          await updateProfile(currentUser, { displayName: trimmedName });
          await errorLogService.logError(
            new Error("Firebase Auth displayName atualizado"),
            {
              component: "PerfilService",
              action: "UpdateFirebaseAuthName",
              userId: currentUser.uid,
              level: "success",
            }
          );
        } catch (authError) {
          await errorLogService.logError(authError, {
            component: "PerfilService",
            action: "UpdateFirebaseAuthNameError",
            userId: currentUser?.uid,
            errorCode: authError.code,
            note: "Continuando com atualização no Firestore",
          });
        }
      }

      // Sempre atualizar no Firestore
      await this.updateFirestoreName(trimmedName, currentUser, churchUser);

      await errorLogService.logError(new Error("Nome atualizado com sucesso"), {
        component: "PerfilService",
        action: "UpdateNameSuccess",
        userId: currentUser?.uid || churchUser?.id,
        newName: trimmedName,
        level: "success",
      });

      return trimmedName;
    } catch (error) {
      await errorLogService.logError(error, {
        component: "PerfilService",
        action: "UpdateNameError",
        userId: currentUser?.uid || churchUser?.id,
        errorCode: error.code,
      });
      throw error;
    }
  }

  /**
   * Atualizar nome no Firestore
   * @param {string} displayName - Nome para salvar
   * @param {Object} currentUser - Usuário atual
   * @param {Object} churchUser - Dados do usuário da igreja
   */
  async updateFirestoreName(displayName, currentUser, churchUser) {
    const updates = {
      displayName,
      nome: displayName, // Manter compatibilidade
    };

    // Determinar documento principal
    const userDocId = churchUser?.id || currentUser?.uid;
    if (userDocId) {
      try {
        const userDoc = doc(db, "users", userDocId);
        await setDoc(userDoc, updates, { merge: true });
      } catch (error) {
        await errorLogService.logError(error, {
          component: "PerfilService",
          action: "UpdateFirestoreUserName",
          userId: userDocId,
          errorCode: error.code,
        });
      }
    }

    // Também atualizar na coleção membros se existir referência
    if (churchUser?.membroId) {
      try {
        const membroDoc = doc(db, "membros", churchUser.membroId);
        await updateDoc(membroDoc, updates);
      } catch (error) {
        await errorLogService.logError(error, {
          component: "PerfilService",
          action: "UpdateFirestoreMembroName",
          membroId: churchUser.membroId,
          errorCode: error.code,
        });
      }
    }
  }

  /**
   * Obter foto de perfil priorizando Google
   * @param {Object} currentUser - Usuário atual
   * @param {Object} churchUser - Dados do usuário da igreja
   * @returns {string|null} URL da foto ou null
   */
  getPhotoURL(currentUser, churchUser) {
    // Priorizar foto do Google se disponível e não for login CPF
    if (currentUser?.photoURL && currentUser?.metodoAuth !== "cpf") {
      return currentUser.photoURL;
    }

    // Senão, usar foto salva no churchUser (pode ter sido uploadada via CPF)
    return churchUser?.photoURL || currentUser?.photoURL || null;
  }

  /**
   * Obter nome de exibição priorizando Google
   * @param {Object} currentUser - Usuário atual
   * @param {Object} churchUser - Dados do usuário da igreja
   * @returns {string} Nome de exibição
   */
  getDisplayName(currentUser, churchUser) {
    // Priorizar displayName do Google se disponível e não for login CPF
    if (currentUser?.displayName && currentUser?.metodoAuth !== "cpf") {
      return currentUser.displayName;
    }

    // Senão, usar nome do churchUser ou displayName atual
    return churchUser?.nome || currentUser?.displayName || "Usuário";
  }

  /**
   * Verificar se usuário pode fazer upload de foto
   * @param {Object} currentUser - Usuário atual
   * @returns {boolean} True se pode fazer upload
   */
  canUploadPhoto(currentUser) {
    // Permitir upload se tiver UID ou email (indicando autenticação válida)
    return currentUser && (currentUser.uid || currentUser.email);
  }

  /**
   * Verificar se usuário pode editar perfil
   * @param {Object} currentUser - Usuário atual
   * @returns {boolean} True se pode editar
   */
  canEditProfile(currentUser) {
    // Permitir edição para qualquer usuário autenticado
    return !!currentUser;
  }
}

// Instância singleton
const perfilService = new PerfilService();

export default perfilService;
